import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Gauge, Pause, Play, RefreshCw, Send, Zap } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { useTenantSlug } from '@/stores/tenantStore';
import { clearRushOverride, fetchRushInsights, setRushOverride } from '@/lib/api/operations';
import type { RushInsights } from '@/lib/api/schemas/operations';
import { useParBreachNotifications, useKpiGuardrailNotifications } from '@/hooks/useNotifications';
import { fetchTenantLocations, updateTenantLocation } from '@/lib/api/settings';
import { updatePosThrottle } from '@/lib/api/pos';
import { sendNotification } from '@/lib/api/notifications';
import { useRushModeEnabled } from '@/hooks/useRushModeEnabled';

const statusVariant: Record<'nominal' | 'watch' | 'critical', 'default' | 'secondary' | 'destructive'> = {
  nominal: 'default',
  watch: 'secondary',
  critical: 'destructive',
};

const formatPercent = (value: number): string => `${Math.round(value * 100)}%`;

interface RushInsightsQueryOptions {
  tenantSlug: string | null | undefined;
  enabled: boolean;
  locationId?: string | null;
  locationReady?: boolean;
}

const useRushInsightsQuery = (options: RushInsightsQueryOptions) =>
  useQuery<RushInsights>({
    queryKey: ['rush-insights', options.tenantSlug ?? 'anonymous', options.locationId ?? 'global'],
    queryFn: () => fetchRushInsights({ locationId: options.locationId ?? undefined }),
    refetchInterval: 15_000,
    enabled: options.enabled && Boolean(options.tenantSlug) && (options.locationReady ?? true),
  });

const RushModePage = (): JSX.Element => {
  const { toast } = useToast();
  const rushModeEnabled = useRushModeEnabled();
  const tenantSlug = useTenantSlug();
  const {
    data: locationsPayload,
    status: locationsStatus,
  } = useQuery({
    queryKey: ['tenant-locations'],
    queryFn: ({ signal }) => fetchTenantLocations(signal),
    staleTime: 5 * 60_000,
  });
  const primaryLocationId = locationsPayload?.locations?.[0]?.id ?? null;
  const locationReady = locationsStatus !== 'loading';
  const { data, isLoading, isError, refetch } = useRushInsightsQuery({
    tenantSlug,
    enabled: rushModeEnabled,
    locationId: primaryLocationId,
    locationReady,
  });
  const locationsRef = useRef<string | null>(null);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [overrideLevel, setOverrideLevel] = useState<'watch' | 'critical'>('critical');
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideDuration, setOverrideDuration] = useState('30');

  useEffect(() => {
    if (primaryLocationId) {
      locationsRef.current = primaryLocationId;
    }
  }, [primaryLocationId]);

  useParBreachNotifications(data?.parBreaches ?? [], {
    disabled: !data,
  });
  useKpiGuardrailNotifications(data?.guardrails ?? [], {
    disabled: !data,
  });
  const activeOverride = data?.override ?? null;
  const manualOverrideSummary = useMemo(() => {
    if (!activeOverride) {
      return 'Use manual override when the line spikes before telemetry catches up.';
    }
    const expiresAt = new Date(activeOverride.expiresAt);
    const formatted = expiresAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return `Holding ${activeOverride.level} until ${formatted}.`;
  }, [activeOverride]);
  const activeOverrideReason = activeOverride?.reason ?? null;
  const activeOverrideUpdatedBy = activeOverride?.updatedBy ?? null;

  const ensureLocationId = useCallback(async () => {
    if (locationsRef.current) {
      return locationsRef.current;
    }
    if (primaryLocationId) {
      locationsRef.current = primaryLocationId;
      return primaryLocationId;
    }
    const payload = await fetchTenantLocations();
    const location = payload.locations[0];
    if (!location) {
      throw new Error('No locations are configured for this tenant.');
    }
    locationsRef.current = location.id;
    return location.id;
  }, [primaryLocationId]);

  const runQuickAction = useCallback(async (actionId: string, fn: () => Promise<void>) => {
    setPendingAction(actionId);
    try {
      await fn();
    } finally {
      setPendingAction(null);
    }
  }, []);

  const handlePauseQr = useCallback(() => {
    void runQuickAction('pause-qr', async () => {
      try {
        const locationId = await ensureLocationId();
        await updateTenantLocation(locationId, {
          qrOrderingStatus: 'paused',
          qrOrderingMessage: 'Paused via Rush Mode',
        });
        toast({
          title: 'QR ordering paused',
          description: 'Guests will see the standard pause message until you resume.',
        });
      } catch (error) {
        toast({
          title: 'Unable to pause QR ordering',
          description: error instanceof Error ? error.message : 'Please try again later.',
          variant: 'destructive',
        });
      }
    });
  }, [ensureLocationId, runQuickAction, toast]);

  const handleResumeQr = useCallback(() => {
    void runQuickAction('resume-qr', async () => {
      try {
        const locationId = await ensureLocationId();
        await updateTenantLocation(locationId, {
          qrOrderingStatus: 'active',
          qrOrderingMessage: null,
        });
        toast({
          title: 'QR ordering resumed',
          description: 'Mobile ordering is live again for guests.',
        });
      } catch (error) {
        toast({
          title: 'Unable to resume QR ordering',
          description: error instanceof Error ? error.message : 'Please try again later.',
          variant: 'destructive',
        });
      }
    });
  }, [ensureLocationId, runQuickAction, toast]);

  const handlePingStaff = useCallback(() => {
    void runQuickAction('ping-staff', async () => {
      try {
        const recipient = tenantSlug ? `${tenantSlug}-ops@braise.invalid` : 'rush-mode@braise.invalid';
        await sendNotification({
          channel: 'email',
          templateId: 'ops.rush-mode.broadcast',
          to: recipient,
          metadata: {
            source: 'rush-mode',
            tenant: tenantSlug,
          },
          data: {
            tenantName: tenantSlug,
            reason: data?.status.reasons[0] ?? 'Rush Mode quick action triggered. Mobilize coverage.',
            triggeredAt: new Date().toISOString(),
          },
        });
        toast({
          title: 'Ping sent',
          description: `Broadcast queued for ${tenantSlug ?? 'ops team'}.`,
        });
      } catch (error) {
        toast({
          title: 'Unable to send ping',
          description: error instanceof Error ? error.message : 'Please try again later.',
          variant: 'destructive',
        });
      }
    });
  }, [data?.status.reasons, runQuickAction, tenantSlug, toast]);

  const handleRefresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  const channelMix = useMemo(() => data?.channelMix ?? [], [data?.channelMix]);
  const busiestStation = useMemo(() => {
    if (!data?.stationLoad?.length) {
      return null;
    }
    return (
      [...data.stationLoad]
        .filter((station) => Boolean(station.stationId))
        .sort((a, b) => b.load - a.load)[0] ?? data.stationLoad[0]
    );
  }, [data?.stationLoad]);

  const handleThrottleStation = useCallback(() => {
    if (!busiestStation?.stationId) {
      toast({
        title: 'No stations available',
        description: 'Station load data is not available right now.',
        variant: 'destructive',
      });
      return;
    }
    const targetLimit = Math.max(4, Math.round((data?.throughput.targetPerMinute ?? 10) / 2));
    void runQuickAction('throttle-station', async () => {
      try {
        await updatePosThrottle(busiestStation.stationId!, {
          mode: 'limit',
          durationMinutes: 10,
          maxOrdersPerMinute: targetLimit,
          reason: 'Rush Mode quick action',
          notes: 'Applied via Rush Mode preset',
        });
        toast({
          title: `Throttle applied to ${busiestStation.name}`,
          description: `Limiting to ${targetLimit} orders/min for 10 minutes.`,
        });
      } catch (error) {
        toast({
          title: 'Unable to throttle station',
          description: error instanceof Error ? error.message : 'Please try again later.',
          variant: 'destructive',
        });
      }
    });
  }, [busiestStation, data?.throughput.targetPerMinute, runQuickAction, toast]);

  const handleApplyOverride = useCallback(() => {
    void runQuickAction('set-override', async () => {
      try {
        const durationMinutes = Number.parseInt(overrideDuration, 10);
        await setRushOverride({
          level: overrideLevel,
          reason: overrideReason.trim().length > 0 ? overrideReason.trim() : undefined,
          durationMinutes: Number.isFinite(durationMinutes) ? durationMinutes : undefined,
        });
        await refetch();
        toast({
          title: 'Manual rush override set',
          description: `Holding ${overrideLevel} for roughly ${Number.isFinite(durationMinutes) ? durationMinutes : 30} minutes.`,
        });
      } catch (error) {
        toast({
          title: 'Unable to set manual rush override',
          description: error instanceof Error ? error.message : 'Please try again later.',
          variant: 'destructive',
        });
      }
    });
  }, [overrideDuration, overrideLevel, overrideReason, refetch, runQuickAction, toast]);

  const handleClearOverride = useCallback(() => {
    void runQuickAction('clear-override', async () => {
      try {
        await clearRushOverride();
        await refetch();
        toast({
          title: 'Manual override cleared',
          description: 'Rush status now follows live telemetry again.',
        });
      } catch (error) {
        toast({
          title: 'Unable to clear manual override',
          description: error instanceof Error ? error.message : 'Please try again later.',
          variant: 'destructive',
        });
      }
    });
  }, [refetch, runQuickAction, toast]);

  if (!rushModeEnabled) {
    return (
      <div className="space-y-4 p-6">
        <Card>
          <CardHeader>
            <CardTitle>Rush Mode disabled</CardTitle>
            <CardDescription>
              Enable the <code>rush.mode</code> feature flag for this tenant to access real-time rush metrics and
              actions.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="space-y-4 p-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Unable to load rush insights</CardTitle>
              <CardDescription>Check your connection or try again shortly.</CardDescription>
            </div>
            <Button variant="outline" onClick={handleRefresh}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry
            </Button>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const { status, throughput, infrastructure, staffing, forecast, afterAction, stationLoad } = data;

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-col gap-4 lg:flex-row">
        <Card className="flex-1">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Rush status</CardTitle>
              <CardDescription>Live view of the last 10 minutes</CardDescription>
            </div>
            <Badge variant={statusVariant[status.level]} className="uppercase tracking-wide">
              {status.level}
            </Badge>
          </CardHeader>
          <CardContent className="space-y-2">
            {status.reasons.map((reason) => (
              <div key={reason} className="flex items-center gap-2 text-sm text-muted-foreground">
                <AlertTriangle className="h-3.5 w-3.5" />
                <span>{reason}</span>
              </div>
            ))}
            <Button variant="outline" size="sm" className="mt-3" onClick={handleRefresh}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh now
            </Button>
          </CardContent>
        </Card>

        <Card className="flex-1">
          <CardHeader>
            <CardTitle>Quick actions</CardTitle>
            <CardDescription>One-tap actions recommended for peak load</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <Button variant="destructive" onClick={handlePauseQr} disabled={pendingAction === 'pause-qr'}>
              <Pause className="mr-2 h-4 w-4" />
              Pause QR
            </Button>
            <Button variant="default" onClick={handleResumeQr} disabled={pendingAction === 'resume-qr'}>
              <Play className="mr-2 h-4 w-4" />
              Resume QR
            </Button>
            <Button
              variant="secondary"
              disabled={pendingAction === 'throttle-station' || !busiestStation?.stationId}
              onClick={handleThrottleStation}
            >
              <Gauge className="mr-2 h-4 w-4" />
              {busiestStation ? `Throttle ${busiestStation.name}` : 'Throttle station'}
            </Button>
            <Button variant="outline" onClick={handlePingStaff} disabled={pendingAction === 'ping-staff'}>
              <Send className="mr-2 h-4 w-4" />
              Ping staff
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <CardTitle>Manual rush override</CardTitle>
            <CardDescription>{manualOverrideSummary}</CardDescription>
            {activeOverrideReason ? (
              <p className="text-sm text-muted-foreground">Reason: {activeOverrideReason}</p>
            ) : null}
            {activeOverrideUpdatedBy ? (
              <p className="text-xs text-muted-foreground">Set by {activeOverrideUpdatedBy}</p>
            ) : null}
          </div>
          {activeOverride ? (
            <Badge variant="secondary" className="uppercase tracking-wide">
              Active
            </Badge>
          ) : null}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-2">
            <Label className="text-sm font-semibold text-muted-foreground">Override level</Label>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant={overrideLevel === 'watch' ? 'default' : 'outline'}
                onClick={() => setOverrideLevel('watch')}
                disabled={pendingAction === 'set-override'}
              >
                Watch
              </Button>
              <Button
                type="button"
                size="sm"
                variant={overrideLevel === 'critical' ? 'default' : 'outline'}
                onClick={() => setOverrideLevel('critical')}
                disabled={pendingAction === 'set-override'}
              >
                Critical
              </Button>
            </div>
          </div>
          <div className="flex flex-col gap-3 lg:flex-row">
            <div className="flex-1">
              <Label htmlFor="override-reason">Reason (optional)</Label>
              <Input
                id="override-reason"
                placeholder="e.g., buses just unloaded at gate"
                maxLength={200}
                value={overrideReason}
                onChange={(event) => setOverrideReason(event.target.value)}
                disabled={pendingAction === 'set-override'}
              />
            </div>
            <div className="w-full lg:w-40">
              <Label htmlFor="override-duration">Duration (min)</Label>
              <Input
                id="override-duration"
                type="number"
                min={5}
                max={180}
                value={overrideDuration}
                onChange={(event) => setOverrideDuration(event.target.value)}
                disabled={pendingAction === 'set-override'}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button onClick={handleApplyOverride} disabled={pendingAction === 'set-override'}>
              Apply override
            </Button>
            <Button
              variant="outline"
              onClick={handleClearOverride}
              disabled={!activeOverride || pendingAction === 'clear-override'}
            >
              Clear override
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Throughput</CardTitle>
            <CardDescription>Orders per minute vs. target</CardDescription>
          </div>
          <Badge variant="outline" className="text-base font-semibold">
            {throughput.ordersPerMinute.toFixed(1)} / {throughput.targetPerMinute}
          </Badge>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <div>
            <p className="text-sm text-muted-foreground">Orders per minute</p>
            <p className="text-2xl font-semibold">{throughput.ordersPerMinute.toFixed(1)}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Backlog size</p>
            <p className="text-2xl font-semibold">{throughput.backlogCount}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Wait delta</p>
            <p className="text-2xl font-semibold">{throughput.waitTimeDeltaSeconds}s</p>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Channel mix</CardTitle>
            <CardDescription>Where the current rush traffic originates</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {channelMix.length === 0 ? (
              <p className="text-sm text-muted-foreground">No orders recorded for this window.</p>
            ) : (
              channelMix.map((row) => (
                <div key={row.channel} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{row.channel}</span>
                    <Badge variant="secondary">{formatPercent(row.percentage)}</Badge>
                  </div>
                  <span className="text-muted-foreground">{row.orders} orders</span>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Infrastructure health</CardTitle>
            <CardDescription>Router + offline queue status</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span>Routers offline</span>
              <span className="font-semibold">{infrastructure.routersOffline}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Offline receipts</span>
              <span className="font-semibold">{infrastructure.offlineQueueSize}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>LTE usage</span>
              <span className="font-semibold">{infrastructure.lteUsageMb.toFixed(1)} MB</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Risk</span>
              <Badge variant={statusVariant[infrastructure.offlineRisk]}>
                {infrastructure.offlineRisk.toUpperCase()}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Station load</CardTitle>
          <CardDescription>Busiest kitchen stations</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {stationLoad.length === 0 ? (
            <p className="text-sm text-muted-foreground">No active stations reporting load.</p>
          ) : (
            stationLoad.map((station) => (
              <div key={station.stationId ?? station.name} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{station.name}</span>
                    {station.throttled && <Badge variant="destructive">Throttled</Badge>}
                  </div>
                  <span className="text-muted-foreground">{Math.round(station.load * 100)}%</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-2 rounded-full bg-primary transition-all"
                    style={{ width: `${Math.min(100, Math.round(station.load * 100))}%` }}
                  />
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Staffing</CardTitle>
            <CardDescription>Active vs. recommended coverage</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span>Active staff</span>
              <span className="font-semibold">{staffing.active}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Recommended</span>
              <span className="font-semibold">{staffing.recommended}</span>
            </div>
            {staffing.gaps.length > 0 ? (
              <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                {staffing.gaps.map((gap) => (
                  <li key={gap}>{gap}</li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground">No staffing gaps detected.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Forecast</CardTitle>
            <CardDescription>Next 15 minutes</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span>Projected orders</span>
              <span className="font-semibold">{forecast.next15MinutesOrders}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Confidence</span>
              <span className="font-semibold">{formatPercent(forecast.confidence)}</span>
            </div>
            <Separator />
            <p className="text-muted-foreground">{forecast.recommendation}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>After-action log</CardTitle>
          <CardDescription>Latest notes and alerts</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {afterAction.length === 0 ? (
            <p className="text-sm text-muted-foreground">No rush events recorded for this window.</p>
          ) : (
            afterAction.map((entry) => (
              <div key={entry.id} className="flex items-start gap-3 rounded-md border border-border/60 p-3">
                {entry.type === 'alert' ? (
                  <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-500" />
                ) : (
                  <Zap className="mt-0.5 h-4 w-4 text-primary" />
                )}
                <div className="text-sm">
                  <p className="font-medium">{entry.message}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(entry.occurredAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RushModePage;
